/**
 * Live inbox notifications for one bot.
 *
 * The socket carries invalidation hints, never conversation content: on any
 * event the caller refetches over HTTPS. That keeps a missed or duplicated
 * event harmless, and means the channel never holds anything sensitive.
 *
 * Reconnects with exponential backoff so a backend restart or a laptop waking
 * from sleep recovers without a page reload.
 */

import { useEffect, useRef } from 'react';

import { getToken } from '../api/client';

const INITIAL_RETRY_MS = 1_000;
const MAX_RETRY_MS = 30_000;

export interface InboxEvent {
  type: 'conversation_started' | 'conversation_updated';
  conversation_id: string;
}

/**
 * Subscribe to inbox activity for `slug`, calling `onEvent` for each message.
 *
 * @param slug - Bot slug, or null to stay disconnected.
 * @param onEvent - Handler for each event. Held in a ref, so it may be an
 *   inline function without causing reconnects.
 */
export function useInboxSocket(
  slug: string | null,
  onEvent: (event: InboxEvent) => void,
): void {
  const handler = useRef(onEvent);
  handler.current = onEvent;

  useEffect(() => {
    const token = getToken();
    if (!slug || !token) return;

    let socket: WebSocket | null = null;
    let retryMs = INITIAL_RETRY_MS;
    let retryTimer: number | undefined;
    let closed = false;

    const connect = (): void => {
      if (closed) return;

      const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
      // No token in the URL: access logs record URLs. It goes as the first
      // frame instead, which the server waits for before sending anything.
      const url = `${scheme}://${window.location.host}/ws/inbox/${slug}`;
      const current = new WebSocket(url);
      socket = current;

      current.onopen = () => {
        retryMs = INITIAL_RETRY_MS;
        current.send(JSON.stringify({ type: 'auth', token }));
      };

      current.onmessage = (event: MessageEvent<string>) => {
        try {
          handler.current(JSON.parse(event.data) as InboxEvent);
        } catch {
          // A frame we cannot parse is not worth tearing the socket down for.
        }
      };

      current.onclose = () => {
        if (closed) return;
        retryTimer = window.setTimeout(connect, retryMs);
        retryMs = Math.min(retryMs * 2, MAX_RETRY_MS);
      };
    };

    connect();

    return () => {
      closed = true;
      if (retryTimer) window.clearTimeout(retryTimer);
      socket?.close();
    };
  }, [slug]);
}
